import { computed, type Ref } from 'vue'
import type {
    JewelleryBraceletProductStyleLabelFragment,
    JewelleryDesignStyleLabelFragment,
    JewelleryEarringsProductStyleLabelFragment,
    JewelleryFragment,
    JewelleryNecklaceProductStyleLabelFragment,
} from '@/graphql/api'
import type { JewelleryProductType } from '@/router'
import { assertNever } from '@/helpers'
import { getJewelleryBreadcrumbs } from '@/views/product/jewellery/getJewelleryBreadcrumbs.ts'
import { useRoutes } from '@/router/routes.ts'

export function useJewelleryDetailsBreadcrumbs(
    jewelleryItem: Readonly<Ref<JewelleryFragment | undefined | null>>,
    productType: Readonly<Ref<JewelleryProductType>>
) {
    const routes = useRoutes()
    const productStyle = computed(
        () =>
            getProductStyle(jewelleryItem.value) as
                | JewelleryBraceletProductStyleLabelFragment
                | JewelleryEarringsProductStyleLabelFragment
                | JewelleryNecklaceProductStyleLabelFragment
    )
    const designStyle = computed(
        () =>
            getItem(
                jewelleryItem.value?.designStyles
            ) as JewelleryDesignStyleLabelFragment
    )
    const metal = computed(() => {
        if (jewelleryItem.value?.primaryMetal) {
            return [jewelleryItem.value?.primaryMetal]
        }
        return []
    })
    const gemstone = computed(() => getItem(jewelleryItem.value?.gemstones))
    return getJewelleryBreadcrumbs(
        {
            productType,
            productStyle,
            designStyle,
            metal,
            gemstone,
            product: computed(() => {
                return {
                    title: jewelleryItem.value?.productName || '',
                }
            }),
        },
        routes
    )
}

function getProductStyle(jewelleryItem: JewelleryFragment | null | undefined) {
    switch (jewelleryItem?.__typename) {
        case 'CatalogueJewelleryNecklace':
            return getItem(jewelleryItem.necklaceProductStyles)
        case 'CatalogueJewelleryBracelet':
            return getItem(jewelleryItem.braceletProductStyles)
        case 'CatalogueJewelleryEarrings':
            return getItem(jewelleryItem.earringsProductStyles)
        case 'CatalogueJewelleryRing':
            return getItem(jewelleryItem.jewelleryRingProductStyles)
        case undefined:
            return undefined
        default:
            return assertNever(jewelleryItem, undefined)
    }
}

function getItem<T>(items: T[] | null | undefined): T | undefined {
    return items?.length === 1 ? items[0] : undefined
}
